/**
 * @lodex-pro/web-portal — App component (public + authenticated route tree).
 *
 * Phase 4 / Plan 04-10 — wraps the app content in an auth gate using `useAuth`
 * from `@lodex-pro/web-shared`.
 *
 * Phase 15 followup (DEC-152) — replaced the scaffold placeholder with a minimal
 * portal home (welcome + tier badge + sign-out).
 *
 * Phase 15 / Task 15.4 (DEC-153) — App becomes the route tree. Auth gate stays
 * at the top. Signed-in users mount `<PortalShell>` (tier-aware sidebar layout)
 * wrapping the six route components. Router provider is bootstrapped in main.tsx
 * so `useNavigate` inside SignInPage works.
 *
 * Phase 15 followup (DEC-155) — public routes declared FIRST so react-router
 * matches /portal/register and /portal/verify before the auth-gated catch-all.
 * PublicLayout provides a minimal branded shell with no auth-dependent UI.
 * Unauthenticated users hitting /portal or any other authenticated route see
 * SignInPage. Authenticated users hitting /portal/register see RegisterPage
 * (public routes always match first — grey area 4 resolution, DEC-155).
 *
 * Phase 16 / DEC-159 — pool-aware sign-in. The route tree is now split into
 * two authenticated subtrees:
 *   - `/portal/operator/*`  — operator subtree (uses `PortalShell` + operator
 *     `/me` API). The legacy top-level routes (`/portal`, `/portal/tickets`,
 *     …) all remain as operator routes for backwards compat.
 *   - `/portal/disposal/*`  — disposal subtree (uses `DisposalShell` +
 *     `DisposalHome` stub; real modules ship in 16.4+).
 * Each subtree wraps its children in a `PortalTenantTypeProvider` so the
 * shared `SignInPage` renders correct branding + navigates to the correct
 * post-signin home. Legacy `/portal/sign-in` is kept as an OPERATOR alias
 * (15.2 approval emails ship this URL; existing users may have bookmarked it).
 *
 * Branding: `lodeX Pro Portal` (tenant-facing brand for operator + disposal users).
 *
 * Phase 16 followup (DEC-160) — adds two disposal-portal public routes
 * (`/portal/disposal/register` + `/portal/disposal/verify`) mirroring the
 * operator public routes but posting to the disposal-portal backend endpoints
 * shipped in 16.1 (DEC-156).
 *
 * Spec: master spec §7 + §81; DEC-152; DEC-153; DEC-155; DEC-159; DEC-160.
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@lodex-pro/web-shared';

import { SignInPage } from './auth/SignInPage';
import { PortalTenantTypeProvider } from './auth/tenant-type-context';
import { PortalShell } from './nav/PortalShell';
import { DisposalShell } from './nav/DisposalShell';
import { PortalHome } from './routes/PortalHome';
import { TicketsList } from './routes/TicketsList';
import { TicketDetail } from './routes/TicketDetail';
import { Volumes } from './routes/Volumes';
import { Reports } from './routes/Reports';
import { Tanks } from './routes/Tanks';
// Phase 18 / Task 18.9 (OPAPI-06) — the real page behind the sidebar's
// PREMIUM-locked "API access" entry, which used to open a stub.
import { ApiUsage } from './routes/ApiUsage';
// Operator portal rework PR 9 — the operator admin issues, rotates and revokes its API keys.
import { ApiKeys } from './routes/ApiKeys';
import { Webhooks } from './routes/Webhooks';
import { DeviceReadings } from './routes/DeviceReadings';
import { Settings } from './routes/Settings';
// Operator portal rework PR 7 — the company's tank records (admin) and routes (admin, foreman).
import { TankSetup } from './routes/TankSetup';
import { PortalRoutes } from './routes/PortalRoutes';
// Operator portal rework PR 8 — the tanks a pumper or foreman looks after; the admin's contacts.
import { MyTanks } from './routes/MyTanks';
// Operator portal rework PR 12a — turning a load in to a hauler, and what one turned in.
import { RequestHaul } from './routes/RequestHaul';
import { MyRequests } from './routes/MyRequests';
import { DisposalHome } from './routes/DisposalHome';
// Phase 16 / Task 16.6 (DEC-163) — disposal BI dashboard.
import { DisposalBiDashboard } from './routes/DisposalBiDashboard';
import { PublicLayout } from './public/PublicLayout';
// Operator portal rework PR 1 — operator self-signup is closed (Paelode creates accounts).
import { SignupClosedPage } from './public/SignupClosedPage';
import { DisposalRegisterPage } from './public/DisposalRegisterPage';
import { DisposalVerifyMagicLinkPage } from './public/DisposalVerifyMagicLinkPage';
// Operator portal rework PR 5 — a Paelode admin viewing the portal as an operator user.
import { ViewAsStartPage, ViewAsEndedPage } from './public/ViewAsStartPage';
import { useViewAs } from './auth/view-as';

// Small helper to keep the JSX below readable — a sign-in page bound to a
// specific tenant type via context provider.
function TypedSignInPage({
  tenantType,
}: {
  readonly tenantType: 'operator' | 'disposal';
}): React.ReactElement {
  return (
    <PortalTenantTypeProvider tenantType={tenantType}>
      <SignInPage />
    </PortalTenantTypeProvider>
  );
}

export function App(): React.ReactElement {
  const { isAuthenticated: signedIn, loading } = useAuth();
  // PR 5 — a view-as session authenticates this tab by its own token (auth/view-as.ts).
  const viewAs = useViewAs();
  const isAuthenticated = viewAs.session !== null || signedIn;

  // PR 5 — once a view-as session has ended in this tab, show that, never the operator sign-in.
  if (viewAs.ended && viewAs.session === null) {
    return (
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/portal/view-as" element={<ViewAsStartPage />} />
          <Route path="*" element={<ViewAsEndedPage />} />
        </Route>
      </Routes>
    );
  }

  if (loading && viewAs.session === null) {
    return <p role="status">Loading…</p>;
  }

  return (
    <Routes>
      {/* PUBLIC — rendered regardless of auth state (DEC-155).
          Declared FIRST so react-router matches these paths before the
          auth-gated catch-all. Authenticated users hitting /portal/register
          or /portal/verify still see the public page (grey area 4 resolution).
          DEC-160: adds disposal-portal public routes under /portal/disposal/*,
          matching DEC-159's pool-aware URL convention. */}
      <Route element={<PublicLayout />}>
        <Route path="/portal/register" element={<SignupClosedPage />} />
        <Route path="/portal/verify" element={<SignupClosedPage />} />
        <Route path="/portal/disposal/register" element={<DisposalRegisterPage />} />
        <Route path="/portal/disposal/verify" element={<DisposalVerifyMagicLinkPage />} />
        <Route path="/portal/view-as" element={<ViewAsStartPage />} />
      </Route>

      {isAuthenticated ? (
        <>
          {/* DEC-159: DISPOSAL authenticated subtree — minimal stub until 16.4+ */}
          <Route
            element={
              <PortalTenantTypeProvider tenantType="disposal">
                <DisposalShell />
              </PortalTenantTypeProvider>
            }
          >
            <Route path="/portal/disposal" element={<DisposalHome />} />
            {/* Phase 16 / Task 16.6 (DEC-163) — BI dashboard. */}
            <Route path="/portal/disposal/bi" element={<DisposalBiDashboard />} />
            <Route path="/portal/disposal/*" element={<DisposalHome />} />
          </Route>
          {/* DEC-159: OPERATOR authenticated subtree — the pre-existing route
              set, exposed both at `/portal/*` (legacy paths) and under the
              explicit `/portal/operator/*` prefix. The two share the same
              components; react-router matches whichever URL the user lands on. */}
          <Route
            element={
              <PortalTenantTypeProvider tenantType="operator">
                <PortalShell />
              </PortalTenantTypeProvider>
            }
          >
            {/* Explicit operator prefix — new post-DEC-159 URL shape */}
            <Route path="/portal/operator" element={<PortalHome />} />
            <Route path="/portal/operator/tickets" element={<TicketsList />} />
            <Route path="/portal/operator/tickets/:id" element={<TicketDetail />} />
            <Route path="/portal/operator/volumes" element={<Volumes />} />
            <Route path="/portal/operator/reports" element={<Reports />} />
            <Route path="/portal/operator/tanks" element={<Tanks />} />
            <Route path="/portal/operator/tank-setup" element={<TankSetup />} />
            <Route path="/portal/operator/routes" element={<PortalRoutes />} />
            <Route path="/portal/operator/my-tanks" element={<MyTanks />} />
            <Route path="/portal/operator/request-haul" element={<RequestHaul />} />
            <Route path="/portal/operator/my-requests" element={<MyRequests />} />
            <Route path="/portal/operator/api-keys" element={<ApiUsage />} />
            <Route path="/portal/operator/api-keys/manage" element={<ApiKeys />} />
            <Route path="/portal/operator/webhooks" element={<Webhooks />} />
            <Route path="/portal/operator/device-readings" element={<DeviceReadings />} />
            <Route path="/portal/operator/settings" element={<Settings />} />
            {/* Legacy operator paths (pre-DEC-159 15.2 users) — same components */}
            <Route path="/" element={<Navigate to="/portal" replace />} />
            <Route path="/portal" element={<PortalHome />} />
            <Route path="/portal/tickets" element={<TicketsList />} />
            <Route path="/portal/tickets/:id" element={<TicketDetail />} />
            <Route path="/portal/volumes" element={<Volumes />} />
            <Route path="/portal/reports" element={<Reports />} />
            <Route path="/portal/tanks" element={<Tanks />} />
            <Route path="/portal/tank-setup" element={<TankSetup />} />
            <Route path="/portal/routes" element={<PortalRoutes />} />
            <Route path="/portal/my-tanks" element={<MyTanks />} />
            <Route path="/portal/request-haul" element={<RequestHaul />} />
            <Route path="/portal/my-requests" element={<MyRequests />} />
            <Route path="/portal/api-keys" element={<ApiUsage />} />
            <Route path="/portal/api-keys/manage" element={<ApiKeys />} />
            <Route path="/portal/webhooks" element={<Webhooks />} />
            <Route path="/portal/device-readings" element={<DeviceReadings />} />
            <Route path="/portal/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/portal/operator" replace />} />
          </Route>
        </>
      ) : (
        <>
          {/* DEC-159: pool-aware sign-in pages — each provides its own tenant type. */}
          <Route
            path="/portal/disposal/sign-in"
            element={<TypedSignInPage tenantType="disposal" />}
          />
          <Route path="/portal/disposal/*" element={<TypedSignInPage tenantType="disposal" />} />
          <Route
            path="/portal/operator/sign-in"
            element={<TypedSignInPage tenantType="operator" />}
          />
          <Route path="/portal/operator/*" element={<TypedSignInPage tenantType="operator" />} />
          {/* Legacy `/portal/sign-in` — kept as operator alias (15.2 emails, bookmarks). */}
          <Route path="/portal/sign-in" element={<TypedSignInPage tenantType="operator" />} />
          <Route path="/" element={<TypedSignInPage tenantType="operator" />} />
          {/* Catch-all for unauthenticated users — /portal/register + /portal/verify
              already matched above and will NOT fall through to this. */}
          <Route path="*" element={<TypedSignInPage tenantType="operator" />} />
        </>
      )}
    </Routes>
  );
}
